import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, XCircle, Package, ArrowsClockwise } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Panel de Servicios (catálogo + Service IDs).
 * - Cada servicio muestra sus paquetes y su external_service_id.
 * - El external_service_id es editable inline y validable contra el panel SMM.
 */
export const ServicesPanel = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // { serviceId: externalId }
  const [validating, setValidating] = useState({}); // { serviceId: bool }
  const [validation, setValidation] = useState({}); // { serviceId: {ok, error, provider_name...} }

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/services`, { withCredentials: true });
      setServices(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const setDraft = (id, value) => setDrafts((d) => ({ ...d, [id]: value }));

  const saveExternalId = async (svc) => {
    const value = (drafts[svc.id] ?? svc.external_service_id ?? '').trim();
    try {
      await axios.patch(
        `${API_URL}/api/admin/services/${svc.id}`,
        { external_service_id: value || null },
        { withCredentials: true }
      );
      toast.success('Service ID guardado');
      setDrafts((d) => { const c = { ...d }; delete c[svc.id]; return c; });
      fetch();
    } catch (e) {
      toast.error('Error al guardar Service ID');
    }
  };

  const validate = async (svc) => {
    setValidating((v) => ({ ...v, [svc.id]: true }));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/services/${svc.id}/validate`,
        {},
        { withCredentials: true }
      );
      setValidation((v) => ({ ...v, [svc.id]: data }));
      if (data.ok) toast.success(`Válido: ${data.provider_name || ''}`);
      else toast.error(data.error || 'ID inválido');
    } catch (e) {
      toast.error('Error al validar');
    } finally {
      setValidating((v) => ({ ...v, [svc.id]: false }));
    }
  };

  const toggleEnabled = async (svc) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/services/${svc.id}`,
        { enabled: !svc.enabled },
        { withCredentials: true }
      );
      toast.success(svc.enabled ? 'Servicio desactivado' : 'Servicio activado');
      fetch();
    } catch (e) {
      toast.error('Error al actualizar servicio');
    }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground text-sm">Cargando servicios...</div>;

  return (
    <div className="space-y-5" data-testid="services-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Servicios & Service IDs</h2>
          <p className="text-sm text-muted-foreground">
            Configurá el <span className="font-mono">Service ID</span> del panel SMM para cada servicio.
            El sistema usará ese ID al enviar pedidos automáticamente.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetch} data-testid="services-refresh">
          <ArrowsClockwise size={14} className="mr-1.5" /> Recargar
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {services.map((svc) => {
          const draftValue = drafts[svc.id];
          const currentValue = draftValue !== undefined ? draftValue : (svc.external_service_id || '');
          const changed = draftValue !== undefined && draftValue !== (svc.external_service_id || '');
          const v = validation[svc.id];
          return (
            <Card key={svc.id} className="p-4 sm:p-5 border-border" data-testid={`service-row-${svc.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Package size={18} weight="duotone" className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{svc.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {svc.platform} · {svc.service_type} · {svc.packages?.length || 0} paquetes
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={svc.enabled ? 'default' : 'outline'} className={svc.enabled ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' : ''}>
                    {svc.enabled ? 'Activo' : 'Inactivo'}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => toggleEnabled(svc)} data-testid={`service-toggle-${svc.id}`}>
                    {svc.enabled ? 'Desactivar' : 'Activar'}
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
                <div className="space-y-1">
                  <Label htmlFor={`sid-${svc.id}`} className="text-xs">Service ID del panel SMM</Label>
                  <Input
                    id={`sid-${svc.id}`}
                    value={currentValue}
                    onChange={(e) => setDraft(svc.id, e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="ej: 1234"
                    inputMode="numeric"
                    data-testid={`service-external-id-${svc.id}`}
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => saveExternalId(svc)}
                  disabled={!changed}
                  data-testid={`service-save-${svc.id}`}
                >
                  Guardar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => validate(svc)}
                  disabled={!currentValue || validating[svc.id]}
                  data-testid={`service-validate-${svc.id}`}
                >
                  {validating[svc.id] ? 'Validando...' : 'Validar en SMM'}
                </Button>
              </div>

              {v && (
                <div
                  className={`mt-2 text-xs p-2 rounded-md flex items-start gap-2 ${
                    v.ok ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'
                  }`}
                  data-testid={`service-validation-${svc.id}`}
                >
                  {v.ok ? <CheckCircle size={14} weight="fill" className="mt-0.5" /> : <XCircle size={14} weight="fill" className="mt-0.5" />}
                  <div>
                    {v.ok ? (
                      <>
                        Válido — <strong>{v.provider_name}</strong>
                        {v.provider_rate && <> · Rate: {v.provider_rate}</>}
                        {v.min && <> · min: {v.min}</>}
                        {v.max && <> · max: {v.max}</>}
                      </>
                    ) : (
                      <>{v.error}</>
                    )}
                  </div>
                </div>
              )}

              {svc.packages && svc.packages.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                    Ver paquetes ({svc.packages.length})
                  </summary>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 text-xs">
                    {svc.packages.map((p, i) => (
                      <div key={i} className="p-2 rounded bg-muted/40 flex flex-col">
                        <span className="font-mono font-semibold">{p.quantity.toLocaleString('es-AR')}</span>
                        <span className="text-muted-foreground">${p.price_ars.toLocaleString('es-AR')}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};
